import * as log from '../util/log';
import { showToast } from '../util/toast';
import { fetchSavedListingsApi, saveListingApi, unsaveListingApi } from '../util/api';
import {
  sendTrackingEvent,
  TRACKING_EVENTS,
  getSaveActionTrackingParams,
  getSaveFailureTrackingParams,
} from '../analytics/trackingEvent';

// ================ Action types ================ //

export const FETCH_SAVED_LISTINGS_REQUEST = 'app/savedListings/FETCH_SAVED_LISTINGS_REQUEST';
export const FETCH_SAVED_LISTINGS_SUCCESS = 'app/savedListings/FETCH_SAVED_LISTINGS_SUCCESS';
export const FETCH_SAVED_LISTINGS_ERROR = 'app/savedListings/FETCH_SAVED_LISTINGS_ERROR';
export const SAVE_LISTING_REQUEST = 'app/savedListings/SAVE_LISTING_REQUEST';
export const SAVE_LISTING_SUCCESS = 'app/savedListings/SAVE_LISTING_SUCCESS';
export const SAVE_LISTING_ERROR = 'app/savedListings/SAVE_LISTING_ERROR';
export const UNSAVE_LISTING_REQUEST = 'app/savedListings/UNSAVE_LISTING_REQUEST';
export const UNSAVE_LISTING_SUCCESS = 'app/savedListings/UNSAVE_LISTING_SUCCESS';
export const UNSAVE_LISTING_ERROR = 'app/savedListings/UNSAVE_LISTING_ERROR';

// ================ Reducer ================ //

const savedKey = (listingId, timeslotId) => {
  const id = typeof listingId === 'string' ? listingId : listingId?.uuid;
  const tid = timeslotId ? (typeof timeslotId === 'string' ? timeslotId : timeslotId?.uuid) : null;
  return tid ? `${id}::${tid}` : `${id}::`;
};

const initialState = {
  savedItems: [],
  fetchInProgress: false,
  fetchError: null,
  saveInProgress: false,
  saveError: null,
  unsaveInProgress: false,
  unsaveError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_SAVED_LISTINGS_REQUEST:
      return { ...state, fetchInProgress: true, fetchError: null };
    case FETCH_SAVED_LISTINGS_SUCCESS: {
      const fetchedItems = Array.isArray(payload) ? payload : [];
      const fetchedKeys = new Set(fetchedItems.map(s => savedKey(s.listingId, s.timeslotId)));
      const optimisticallyAdded = (state.savedItems || []).filter(
        s => !fetchedKeys.has(savedKey(s.listingId, s.timeslotId))
      );
      return {
        ...state,
        savedItems: [...fetchedItems, ...optimisticallyAdded],
        fetchInProgress: false,
        fetchError: null,
      };
    }
    case FETCH_SAVED_LISTINGS_ERROR:
      return { ...state, fetchInProgress: false, fetchError: payload };
    case SAVE_LISTING_REQUEST:
      return { ...state, saveInProgress: true, saveError: null };
    case SAVE_LISTING_SUCCESS: {
      const { listingId, timeslotId, timeslotStart, timeslotEnd } = payload;
      const key = savedKey(listingId, timeslotId);
      if (state.savedItems.some(s => savedKey(s.listingId, s.timeslotId) === key)) {
        return { ...state, saveInProgress: false, saveError: null };
      }
      return {
        ...state,
        savedItems: [
          ...state.savedItems,
          {
            listingId,
            timeslotId: timeslotId || null,
            timeslotStart: timeslotStart || null,
            timeslotEnd: timeslotEnd || null,
          },
        ],
        saveInProgress: false,
        saveError: null,
      };
    }
    case SAVE_LISTING_ERROR:
      return { ...state, saveInProgress: false, saveError: payload };
    case UNSAVE_LISTING_REQUEST:
      return { ...state, unsaveInProgress: true, unsaveError: null };
    case UNSAVE_LISTING_SUCCESS: {
      const { listingId, timeslotId } = payload;
      const key = savedKey(listingId, timeslotId);
      return {
        ...state,
        savedItems: state.savedItems.filter(s => savedKey(s.listingId, s.timeslotId) !== key),
        unsaveInProgress: false,
        unsaveError: null,
      };
    }
    case UNSAVE_LISTING_ERROR:
      return { ...state, unsaveInProgress: false, unsaveError: payload };
    default:
      return state;
  }
}

// ================ Selectors ================ //

export const getSavedItems = state => state.savedListings?.savedItems || [];

export const isListingSaved = (state, listingId, timeslotId) => {
  const items = getSavedItems(state);
  const key = savedKey(listingId, timeslotId);
  return items.some(s => savedKey(s.listingId, s.timeslotId) === key);
};

const toComparableIso = v => {
  if (!v) return null;
  const s =
    typeof v === 'string' ? v : typeof v?.toISOString === 'function' ? v.toISOString() : null;
  return s ? new Date(s).toISOString() : null;
};

export const isListingSavedByDates = (state, listingId, timeslotStart, timeslotEnd) => {
  const items = getSavedItems(state);
  if (!listingId || !timeslotStart || !timeslotEnd) return false;
  const lid = typeof listingId === 'string' ? listingId : listingId?.uuid;
  const start = toComparableIso(timeslotStart);
  const end = toComparableIso(timeslotEnd);
  return items.some(
    s =>
      (typeof s.listingId === 'string' ? s.listingId : s.listingId?.uuid) === lid &&
      toComparableIso(s.timeslotStart) === start &&
      toComparableIso(s.timeslotEnd) === end
  );
};

/** Returns the saved item matched by listingId + timeslot dates, or null. Used for unsave when firstTimeslot id may not match. */
export const getSavedItemByDates = (state, listingId, timeslotStart, timeslotEnd) => {
  const items = getSavedItems(state);
  if (!listingId || !timeslotStart || !timeslotEnd) return null;
  const lid = typeof listingId === 'string' ? listingId : listingId?.uuid;
  const start = toComparableIso(timeslotStart);
  const end = toComparableIso(timeslotEnd);
  return (
    items.find(
      s =>
        (typeof s.listingId === 'string' ? s.listingId : s.listingId?.uuid) === lid &&
        toComparableIso(s.timeslotStart) === start &&
        toComparableIso(s.timeslotEnd) === end
    ) || null
  );
};

export const fetchSavedListingsInProgress = state => state.savedListings?.fetchInProgress || false;

// ================ Action creators ================ //

// ================ Thunks ================ //

const getListingId = listing =>
  typeof listing === 'string' ? listing : listing?.uuid ?? listing?.id?.uuid;
const getTimeslotId = timeslot =>
  !timeslot ? null : typeof timeslot === 'string' ? timeslot : timeslot?.uuid ?? timeslot?.id?.uuid;
const getTimeslotDates = timeslot => {
  if (!timeslot?.attributes) return [null, null];
  const start = timeslot.attributes.start;
  const end = timeslot.attributes.end;
  const toIso = v =>
    !v
      ? null
      : typeof v === 'string'
      ? v
      : typeof v?.toISOString === 'function'
      ? v.toISOString()
      : null;
  return [toIso(start), toIso(end)];
};

export const fetchSavedListings = () => dispatch => {
  dispatch({ type: FETCH_SAVED_LISTINGS_REQUEST });
  return fetchSavedListingsApi()
    .then(res => {
      const data = res?.data ?? res;
      const items = Array.isArray(data) ? data : [];
      dispatch({ type: FETCH_SAVED_LISTINGS_SUCCESS, payload: items });
    })
    .catch(e => {
      log.error(e, 'fetch-saved-listings-failed');
      dispatch({ type: FETCH_SAVED_LISTINGS_ERROR, payload: e });
    });
};

export const saveListing = (listing, timeslot = null, options = {}) => (dispatch, getState) => {
  const listingId = getListingId(listing);
  const timeslotId = getTimeslotId(timeslot);
  const [timeslotStart, timeslotEnd] = getTimeslotDates(timeslot);
  if (!listingId) return Promise.reject(new Error('listingId is required'));

  const { source } = options;

  dispatch({ type: SAVE_LISTING_REQUEST });
  dispatch({
    type: SAVE_LISTING_SUCCESS,
    payload: { listingId, timeslotId, timeslotStart, timeslotEnd },
  });
  return saveListingApi(listingId, timeslotId, timeslotStart, timeslotEnd)
    .then(res => {
      const savedItems = getState().savedListings?.savedItems || [];
      const savedCountAfter = savedItems.length;
      if (source) {
        sendTrackingEvent({
          name: TRACKING_EVENTS.SAVED_LISTING,
          parameters: getSaveActionTrackingParams(listing, timeslot, getState, {
            action: 'save',
            source,
            savedCountAfter,
          }),
        });
      }
      showToast(
        { messageKey: 'Toast.listingSaved' },
        {
          link: {
            routeName: 'SavedEventsPage',
            labelKey: 'Toast.viewSavedEvents',
          },
        }
      );
    })
    .catch(e => {
      if (source) {
        sendTrackingEvent({
          name: TRACKING_EVENTS.SAVE_ACTION_FAILED,
          parameters: getSaveFailureTrackingParams(
            listing,
            timeslot,
            getState,
            {
              action: 'save',
              source,
            },
            e
          ),
        });
      }
      log.error(e, 'save-listing-failed');
      dispatch({ type: UNSAVE_LISTING_SUCCESS, payload: { listingId, timeslotId } });
      dispatch({ type: SAVE_LISTING_ERROR, payload: e });
      showToast({ messageKey: 'Toast.listingSaveFailed' });
    });
};

export const unsaveListing = (listing, timeslot = null, options = {}) => (dispatch, getState) => {
  const listingId = getListingId(listing);
  let timeslotId = getTimeslotId(timeslot);
  const [timeslotStart, timeslotEnd] = getTimeslotDates(timeslot);
  if (!listingId) return Promise.reject(new Error('listingId is required'));
  const { skipToast = false, source } = options;

  const savedItems = getSavedItems(getState());
  const lid = typeof listingId === 'string' ? listingId : listingId;
  const savedMatch =
    savedItems.find(s => {
      const sLid = typeof s.listingId === 'string' ? s.listingId : s.listingId?.uuid;
      if (sLid !== lid) return false;
      if (timeslotId) {
        const sTid = s.timeslotId
          ? typeof s.timeslotId === 'string'
            ? s.timeslotId
            : s.timeslotId?.uuid
          : null;
        return sTid === timeslotId;
      }
      if (timeslotStart && timeslotEnd) {
        return (
          toComparableIso(s.timeslotStart) === toComparableIso(timeslotStart) &&
          toComparableIso(s.timeslotEnd) === toComparableIso(timeslotEnd)
        );
      }
      return !s.timeslotId;
    }) ||
    savedItems.find(s => {
      const sLid = typeof s.listingId === 'string' ? s.listingId : s.listingId?.uuid;
      return sLid === lid;
    });

  if (savedMatch) {
    timeslotId = savedMatch.timeslotId
      ? typeof savedMatch.timeslotId === 'string'
        ? savedMatch.timeslotId
        : savedMatch.timeslotId?.uuid
      : null;
  }

  const resolvedTimeslotStart = savedMatch?.timeslotStart || timeslotStart || null;
  const resolvedTimeslotEnd = savedMatch?.timeslotEnd || timeslotEnd || null;
  const rollbackPayload = savedMatch
    ? {
        listingId: savedMatch.listingId || listingId,
        timeslotId: savedMatch.timeslotId || timeslotId || null,
        timeslotStart: savedMatch.timeslotStart || null,
        timeslotEnd: savedMatch.timeslotEnd || null,
      }
    : {
        listingId,
        timeslotId,
        timeslotStart: resolvedTimeslotStart,
        timeslotEnd: resolvedTimeslotEnd,
      };

  dispatch({ type: UNSAVE_LISTING_REQUEST });
  dispatch({ type: UNSAVE_LISTING_SUCCESS, payload: { listingId, timeslotId } });
  return unsaveListingApi(listingId, timeslotId, resolvedTimeslotStart, resolvedTimeslotEnd)
    .then(() => {
      const savedItems = getState().savedListings?.savedItems || [];
      const savedCountAfter = savedItems.length;
      if (source) {
        sendTrackingEvent({
          name: TRACKING_EVENTS.UNSAVED_LISTING,
          parameters: getSaveActionTrackingParams(listing, timeslot, getState, {
            action: 'unsave',
            source,
            savedCountAfter,
          }),
        });
      }
      if (!skipToast) {
        showToast({ messageKey: 'Toast.listingUnsaved' });
      }
    })
    .catch(e => {
      if (source) {
        sendTrackingEvent({
          name: TRACKING_EVENTS.SAVE_ACTION_FAILED,
          parameters: getSaveFailureTrackingParams(
            listing,
            timeslot,
            getState,
            {
              action: 'unsave',
              source,
            },
            e
          ),
        });
      }
      log.error(e, 'unsave-listing-failed');
      dispatch({ type: SAVE_LISTING_SUCCESS, payload: rollbackPayload });
      dispatch({ type: UNSAVE_LISTING_ERROR, payload: e });
      showToast({ messageKey: 'Toast.listingUnsaveFailed' });
    });
};
